import { ActionIcon, Badge, Divider, Group, Paper, Stack, Table, Text, Tooltip } from "@mantine/core";
import { IconFolder, IconX } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { httpPostGraphql } from "shared/api/httpClient";
import { PropertyBadge } from "shared/components/global/PropertyBadge";
import { getRelativeTime } from "shared/components/RelativeTime";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { permissionCheck, usePermissions } from "shared/stores/oidc";
import { titlecase } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { Cm, Language, Permission, UserStatus } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { match } from "ts-pattern";

export const UserDrawer = (props: { userId: string }) => {
    const { permissions } = usePermissions();
    const { data } = useSuspenseQuery(query(props.userId));

    const removeUserFromGroup = useGraphqlMutation(
        graphql(`
            mutation UserDrawerRemoveUserFromGroup($groupId: UUID!, $userId: UUID!) {
                removeUserFromGroup(groupId: $groupId, userId: $userId) {
                    id
                }
            }
        `),
    );
    const removeUserFromTopic = useGraphqlMutation(
        graphql(`
            mutation UserDrawerRemoveUserFromTopic($topicId: UUID!, $userId: UUID!) {
                removeUserFromTopic(topicId: $topicId, userId: $userId) {
                    id
                }
            }
        `),
    );

    return (
        <Stack>
            <Text size="sm" c="dimmed" mt="-md" style={{ zIndex: 1000 }}>
                {data.loginEmail}
            </Text>
            <Group gap="xs" w="fit-content">
                <Badge variant="light" color={data.language != Language.English ? "yellow" : "gray"}>
                    {titlecase(data.language)}
                </Badge>
                {match(data.status)
                    .with(UserStatus.Active, () => (
                        <Tooltip label={`Last active: ${data.statusSince}`}>
                            <Badge variant="light" color="green">
                                {data.status}
                            </Badge>
                        </Tooltip>
                    ))
                    .with(UserStatus.Invited, () => (
                        <Tooltip label={`Invited on ${data.statusSince}`}>
                            <Badge variant="light" color="yellow">
                                {data.status}
                            </Badge>
                        </Tooltip>
                    ))
                    .with(UserStatus.Inactive, () => (
                        <Tooltip label={`Inactive since: ${data.statusSince}`}>
                            <Badge variant="light" color="red">
                                {data.status}
                            </Badge>
                        </Tooltip>
                    ))
                    .with(UserStatus.OptIn, () => (
                        <Tooltip label={`Joined on ${data.statusSince}`}>
                            <Badge variant="light" color="orange">
                                {data.status}
                            </Badge>
                        </Tooltip>
                    ))
                    .exhaustive()}

                <PropertyBadge title="Last Login">{maybe(data.lastLogin)?.map(parseInt).take(getRelativeTime) ?? "Never"}</PropertyBadge>
                <PropertyBadge title="SMS/Phone Euds">
                    {data.euds.filter((it) => it.type == Cm.Sms || it.type == Cm.Phone).length}
                </PropertyBadge>
                <PropertyBadge title="Native App EUDS">
                    {data.euds.filter((it) => it.type == Cm.Android || it.type == Cm.Ios).length}
                </PropertyBadge>
            </Group>

            <Divider mx="-md" />

            <Text fw="bold">Roles</Text>
            <Paper withBorder shadow="md" p="sm">
                <Table withRowBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Role</Table.Th>
                            <Table.Th>Target</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {data.roles.length == 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={2} align="center">
                                    <Text size="sm" c="dimmed" fs="italic">
                                        No Roles
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {data.roles.map((each, i) => (
                            <Table.Tr key={i}>
                                <Table.Td>
                                    <Tooltip label={each.role.name}>
                                        <Badge variant="default">{each.role.name}</Badge>
                                    </Tooltip>
                                </Table.Td>
                                <Table.Td>
                                    {maybe(each.group)?.take((it) =>
                                        permissionCheck(permissions, Permission.GroupSubgroupManage) ? (
                                            <Link
                                                to="/organization/groups/$groupId"
                                                params={{ groupId: it.id }}
                                                style={{ textDecoration: "underline" }}
                                            >
                                                {it.name}
                                            </Link>
                                        ) : (
                                            <Text>{it.name}</Text>
                                        ),
                                    )}
                                    {maybe(each.topic)?.take((it) =>
                                        permissionCheck(permissions, Permission.TopicCategoryManage) && !it.parent?.id ? (
                                            <Text fz="sm" td="none">
                                                {it.name}
                                            </Text>
                                        ) : (
                                            <Link
                                                to="/organization/topics/$topicId"
                                                params={{ topicId: it.id }}
                                                style={{ textDecoration: "underline" }}
                                            >
                                                {it.name}
                                            </Link>
                                        ),
                                    )}
                                    {!each.group && !each.topic ? <Text size="sm">None</Text> : null}
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>

            <Text fw="bold">Contact Methods</Text>
            <Paper withBorder shadow="md" p="sm">
                <Table.ScrollContainer minWidth={0}>
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Name</Table.Th>
                                <Table.Th>Type</Table.Th>
                                <Table.Th>Verification</Table.Th>
                                <Table.Th>Value</Table.Th>
                                <Table.Th />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {data.euds.length == 0 && (
                                <Table.Tr>
                                    <Table.Td colSpan={4} align="center">
                                        <Text size="sm" c="dimmed" fs="italic">
                                            No EUDs
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                            {data.euds.map((eud) => (
                                <Table.Tr key={eud.id}>
                                    <Table.Td>{eud.name}</Table.Td>
                                    <Table.Td miw={100}>{<Badge variant="default">{eud.type}</Badge>}</Table.Td>
                                    <Table.Td>
                                        {
                                            <Badge variant="light" color={eud.isVerified ? "green" : "red"}>
                                                {eud.isVerified ? "Verified" : "Not Verified"}
                                            </Badge>
                                        }
                                    </Table.Td>
                                    <Table.Td style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                        <span>{eud.value}</span>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>

            <Text fw="bold">Groups</Text>
            <Paper withBorder shadow="md" p="sm">
                <Table.ScrollContainer minWidth={0}>
                    <Table>
                        <Table.Tbody>
                            {data.groups.length == 0 && (
                                <Table.Tr>
                                    <Table.Td colSpan={4} align="center">
                                        <Text size="sm" c="dimmed" fs="italic">
                                            No Groups
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                            {data.groups.map((group) => (
                                <Table.Tr key={group.id}>
                                    <Table.Td>
                                        <Badge variant="default">{group.name}</Badge>
                                    </Table.Td>
                                    <Table.Td w={25}>
                                        {permissionCheck(permissions, Permission.GroupUsersManage) && (
                                            <Tooltip label="Remove user from group">
                                                <ActionIcon
                                                    variant="transparent"
                                                    onClick={() =>
                                                        void removeUserFromGroup
                                                            .mutateAsync({ groupId: group.id, userId: props.userId })
                                                            .then(() =>
                                                                queryClient.invalidateQueries({ queryKey: ["user-drawer", props.userId] }),
                                                            )
                                                            .then(() => notify.show.success("Removed user from group"))
                                                            .catch(notify.catch)
                                                    }
                                                >
                                                    <IconX size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>

            <Text fw="bold">Topics</Text>
            <Paper withBorder shadow="md" p="sm">
                <Table.ScrollContainer minWidth={0}>
                    <Table>
                        <Table.Tbody>
                            {data.topics.length == 0 && (
                                <Table.Tr>
                                    <Table.Td colSpan={4} align="center">
                                        <Text size="sm" c="dimmed" fs="italic">
                                            No Topics
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                            {data.topics
                                .sort((a, b) => {
                                    if (a.isCategory && !b.isCategory) return -1;
                                    if (b.isCategory && !a.isCategory) return 1;
                                    if (a.isRequired && !b.isRequired) return 1;
                                    if (b.isRequired && !a.isRequired) return -1;
                                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                                })
                                .map((topic) => (
                                    <Table.Tr key={topic.id}>
                                        <Table.Td>
                                            <Group gap="xs">
                                                {topic.isCategory && (
                                                    <Tooltip label="Category">
                                                        <IconFolder size={16} />
                                                    </Tooltip>
                                                )}
                                                <Tooltip label="Required" disabled={!topic.isRequired}>
                                                    <Badge variant="default">
                                                        {topic.name}
                                                        {topic.isRequired && (
                                                            <span style={{ color: "var(--input-asterisk-color)" }}>*</span>
                                                        )}
                                                    </Badge>
                                                </Tooltip>
                                            </Group>
                                        </Table.Td>
                                        <Table.Td w={25}>
                                            {permissionCheck(permissions, Permission.TopicUsersManage) && (
                                                <Tooltip
                                                    label={
                                                        topic.isRequired
                                                            ? "Cannot remove user from required topic"
                                                            : "Remove user from topic"
                                                    }
                                                >
                                                    <ActionIcon
                                                        variant="transparent"
                                                        disabled={topic.isRequired}
                                                        style={{ background: "none" }}
                                                        onClick={() =>
                                                            void removeUserFromTopic
                                                                .mutateAsync({ topicId: topic.id, userId: props.userId })
                                                                .then(() =>
                                                                    queryClient.invalidateQueries({
                                                                        queryKey: ["user-drawer", props.userId],
                                                                    }),
                                                                )
                                                                .then(() => notify.show.success("Removed user from topic"))
                                                                .catch(notify.catch)
                                                        }
                                                    >
                                                        <IconX size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>
        </Stack>
    );
};

const query = (id: string) =>
    queryOptions({
        queryKey: ["user-drawer", id],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query UserDrawer($id: UUID!) {
                        user(userId: $id) {
                            id
                            name
                            loginEmail
                            lastLogin
                            status
                            statusSince
                            euds {
                                id
                                type
                                name
                                isVerified
                                value
                            }
                            roles {
                                role {
                                    name
                                }
                                group {
                                    id
                                    name
                                }
                                topic {
                                    id
                                    name
                                    parent {
                                        id
                                    }
                                }
                            }
                            addresses {
                                id
                            }
                            language
                            accessTokens {
                                id
                                name
                                expiration
                                lastUsedAt
                                isExpired
                            }
                            groups {
                                id
                                name
                            }
                            topics {
                                id
                                name
                                isRequired
                                isCategory
                            }
                        }
                    }
                `),
                { id },
            ),
        select: ({ user }) => user,
    });
